'use client';

import { createContext, useContext, useMemo, useCallback } from 'react';
import {
  captureException,
  captureMessage,
  setContext,
  setUser,
} from 'src/observability';
import { env } from 'src/constants';
import { AnalyticsBrowser } from '@segment/analytics-next';

// Extend Window interface to include analytics
declare global {
  interface Window {
    analytics: any;
  }
}

// Disabled for MKT-669: the backend (medscout-api `Profile.set_segment_properties`)
// owns Segment company updates and sends `{ name, hubspot_company_id }`. This call
// sends name only on every login, so once the web source is connected it would
// overwrite HubSpot company names. Before re-enabling, check which destinations the
// web source feeds (Segment Connections tab) and add a Destination Filter that drops
// web-source `group` events before HubSpot (MKT-676 playbook Part 4B).
const SEND_SEGMENT_GROUP = false;

const AnalyticsContext = createContext(undefined);

export const AnalyticsProvider = (props) => {
  const analytics = useMemo(() => {
    const instance = AnalyticsBrowser.load({ writeKey: env.ANALYTICS_ID });

    // Expose analytics globally for Intercom and other integrations
    if (typeof window !== 'undefined') {
      window.analytics = instance;
    }

    return instance;
  }, []);

  return <AnalyticsContext.Provider value={analytics} {...props} />;
};

export const useLogging = () => {
  const analytics = useContext(AnalyticsContext);

  if (!analytics)
    throw new Error('useLogging must be inside an AnalyticsProvider');

  const identify = useCallback(
    (user: MedScout.User) => {
      if (!user?.company) return;

      // Initialize user in Sentry + Datadog RUM
      setUser({
        id: `${user.id}`,
        email: user.email,
        username: user.username,
      });

      // Track user in Segment
      analytics.identify(`${user.id}`, {
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        username: user.username,
        company: user.company.name,
        department: user.company.department,
        isManager: user.company.manager,
        isStaff: user.is_staff,
        isActive: user.is_active,
        isLIREligible: user.is_lir_eligible,
      });

      // Associate user with company in Segment
      if (SEND_SEGMENT_GROUP) {
        analytics.group(`${user.company.id}`, {
          name: user.company.name,
        });
      }
    },
    [analytics]
  );

  return useMemo(
    () => ({
      event: analytics.track,
      page: analytics.page,
      identify,
      alert: captureMessage,
      exception: captureException,
      context: setContext,
    }),
    [analytics, identify]
  );
};
